import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, Subject, map, tap } from 'rxjs';

/**
 * Eine Einheit mit Datum in der Zukunft — meist eine falsch gestellte
 * Geräteuhr in der FIT-Datei (Backend: training_analysis/services/future_sessions.py).
 * Der Import legt sie an, schaltet sie aber stumm: sie zählt in keiner
 * Auswertung, bis das Datum korrigiert oder die Einheit gelöscht ist.
 */
export interface FutureSession {
  session_id: number;
  user_id: number;
  athlete_name: string | null;
  date: string;
  start: string | null;
  sport: string | null;
  sub_sport: string | null;
  name: string | null;
  is_muted: boolean;
  imported_at: string | null;
}

export interface FutureSessionCorrection {
  session_id: number;
  old_date: string;
  new_date: string;
  unmuted: boolean;
  basis_saved: boolean;
}

/** Fachliche Fehlercodes des Backends (gehen 1:1 in i18n-Schlüssel). */
export type FutureSessionErrorCode =
  | 'date_in_future' | 'date_too_early' | 'date_invalid' | 'conflict' | 'not_future' | 'not_found';

interface Envelope<T> { data: T | null; error: string | null }

@Injectable({ providedIn: 'root' })
export class FutureSessionService {
  private readonly http = inject(HttpClient);
  private readonly base = '/api/training_analysis/future-sessions';

  /** Feuert nach jeder Korrektur/Löschung — Kalender und Hinweis laden neu. */
  readonly changed$ = new Subject<void>();

  list(): Observable<FutureSession[]> {
    return this.http
      .get<Envelope<{ sessions: FutureSession[]; count: number }>>(`${this.base}/`)
      .pipe(map((r) => r?.data?.sessions ?? []));
  }

  /** `date` als YYYY-MM-DD (Ortstag). */
  correctDate(sessionId: number, date: string): Observable<FutureSessionCorrection | null> {
    return this.http
      .post<Envelope<FutureSessionCorrection>>(`${this.base}/${sessionId}/correct-date/`, { date })
      .pipe(map((r) => r?.data ?? null), tap(() => this.changed$.next()));
  }

  delete(sessionId: number): Observable<unknown> {
    return this.http
      .post(`${this.base}/${sessionId}/delete/`, {})
      .pipe(tap(() => this.changed$.next()));
  }
}
